"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search } from "lucide-react";
import { ARCHIVES_DB, ARCHIVE_CATEGORIES } from "@/data/archivesData";
const ArchiveExplorer = () => {
  const $ = _c(41);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("All Collections");
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const handleFilter = e => {
        if (e.detail?.category) {
          setActiveCategory(e.detail.category);
          setSearchTerm("");
        }
      };
      window.addEventListener("archive-filter", handleFilter);
      return () => window.removeEventListener("archive-filter", handleFilter);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== activeCategory || $[3] !== searchTerm) {
    t2 = ARCHIVES_DB.filter(item => {
      const matchesCategory = activeCategory === "All Collections" || item.category === activeCategory;
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase()) || item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
    $[2] = activeCategory;
    $[3] = searchTerm;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const filteredArchives = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex items-center gap-2 sm:gap-3 mb-4"><div className="w-8 h-px bg-[#DF6D2A]" /><span className="font-body text-[10.5px] tracking-[0.25em] uppercase text-[#DF6D2A]">Browse Collections</span></div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mb-8 sm:mb-12">{t3}<h2 className="font-display text-[28px] sm:text-[36px] md:text-[44px] text-[#0F1C2E] leading-[1.05]">Explore the <em className="text-[#C9A84C] italic">Archive</em></h2><p className="font-body text-[14px] sm:text-[15px] text-[#4C596B] mt-3 max-w-[640px] leading-[1.7]">Search and filter digitized records across documents, photographs, newspapers, oral histories, ships and routes, and research publications.</p></div>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_0 => setSearchTerm(e_0.target.value);
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== searchTerm) {
    t6 = <input type="text" placeholder="Search archives..." value={searchTerm} onChange={t5} className="w-full bg-white border border-[#0F1C2E]/20 px-4 py-3 pl-10 font-body text-[13px] outline-none focus:border-[#DF6D2A] transition-colors" />;
    $[8] = searchTerm;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0F1C2E]/40" size={16} />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6) {
    t8 = <div className="relative mb-10">{t6}{t7}</div>;
    $[11] = t6;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <h3 className="font-body text-[10px] tracking-[0.2em] uppercase text-[#4C596B] mb-3 sm:mb-4 border-b border-[#0F1C2E]/10 pb-2">Archive Sections</h3>;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== activeCategory) {
    t10 = ARCHIVE_CATEGORIES.map(cat => <li key={cat}><button onClick={() => setActiveCategory(cat)} className={`font-body text-[12px] sm:text-[13px] tracking-wide text-left w-full px-2 sm:px-3 py-1.5 sm:py-2 transition-colors ${activeCategory === cat ? "bg-[#DF6D2A] text-white" : "text-[#0F1C2E] hover:bg-[#EBE5D9]"}`}>{cat}</button></li>);
    $[14] = activeCategory;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10) {
    t11 = <div className="mb-6 sm:mb-10">{t9}<ul className="flex flex-row lg:flex-col flex-wrap gap-2 lg:gap-0 lg:space-y-2">{t10}</ul></div>;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p className="font-body text-[10px] tracking-[0.2em] uppercase text-[#DF6D2A] mb-2">Displaying</p>;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== filteredArchives.length) {
    t13 = <p className="font-display text-[32px] leading-none mb-1">{filteredArchives.length}</p>;
    $[19] = filteredArchives.length;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <p className="font-body text-[12px] text-white/50">historical records</p>;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t13) {
    t15 = <div className="p-6 bg-[#0F1C2E] text-[#F4EFE6]">{t12}{t13}{t14}</div>;
    $[22] = t13;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t11 || $[25] !== t15 || $[26] !== t8) {
    t16 = <div className="w-full lg:w-[280px] flex-shrink-0"><div className="lg:sticky lg:top-32">{t8}{t11}{t15}</div></div>;
    $[24] = t11;
    $[25] = t15;
    $[26] = t8;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== filteredArchives.length) {
    t17 = <span className="text-[#DF6D2A] text-[13px] sm:text-[14px] md:text-[16px] italic">({filteredArchives.length} Results)</span>;
    $[28] = filteredArchives.length;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== activeCategory || $[31] !== t17) {
    t18 = <div className="flex justify-between items-center mb-8 border-b border-[#0F1C2E]/10 pb-4"><p className="font-display text-[18px] sm:text-[22px] md:text-[24px] text-[#0F1C2E]">{activeCategory} {t17}</p></div>;
    $[30] = activeCategory;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== filteredArchives) {
    t19 = filteredArchives.length === 0 ? <div className="text-center py-32 border border-dashed border-[#0F1C2E]/20"><p className="font-display text-[24px] text-[#4C596B] mb-2">No records found.</p><p className="font-body text-[13px] text-[#4C596B]/60">Try adjusting your search terms or filters.</p></div> : <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6"><AnimatePresence>{filteredArchives.map(_temp)}</AnimatePresence></div>;
    $[33] = filteredArchives;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t18 || $[36] !== t19) {
    t20 = <div className="w-full flex-1">{t18}{t19}</div>;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t16 || $[39] !== t20) {
    t21 = <section id="archive-explorer" className="bg-[#F4EFE6] py-10 sm:py-16 md:py-20 relative min-h-screen scroll-mt-[106px]"><div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12">{t4}<div className="flex flex-col lg:flex-row gap-8 sm:gap-10 md:gap-12">{t16}{t20}</div></div></section>;
    $[38] = t16;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  return t21;
};
export default ArchiveExplorer;
function _temp(item_0) {
  return <motion.div key={item_0.id} layout={true} initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} transition={{
    duration: 0.3
  }} className="group bg-white border border-[#0F1C2E]/10 overflow-hidden w-full h-[360px] sm:h-[400px]"><div className="relative w-full h-full overflow-hidden bg-[#0F1C2E]"><img src={item_0.image} alt={item_0.title} className="w-full h-full object-cover grayscale-[40%] sepia-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" /></div></motion.div>;
}